// Copyright (c) 2024-2026 Noushad Khalid / Codeteki. All rights reserved.
// Licensed under the Elastic License 2.0 — see LICENSE for details.

import { ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";

const problems = [
  {
    emoji: "📞",
    problem: "Losing leads after hours",
    detail: "Enquiries land at 9pm. By 8am they've called someone else. You never even knew they were interested.",
    fix: "AI Chat Widget captures every lead 24/7 — name, job, contact — and sends it straight to you.",
    tool: "AI Chat Widget",
    href: "/ai-chat-widget",
    cta: "Book a Demo",
    accent: "from-red-50 to-orange-50",
    border: "border-red-100 hover:border-[#f9cb07]",
  },
  {
    emoji: "⏱️",
    problem: "Quotes take too long — or don't exist",
    detail: "Visitors want a price. You're on the tools. They leave and book whoever gave them a number first.",
    fix: "Instant Quote Widget gives visitors a ballpark in seconds. You get a qualified lead, not a missed call.",
    tool: "Instant Quote Widget",
    href: "/instant-quote-widget",
    cta: "See It Live",
    accent: "from-amber-50 to-yellow-50",
    border: "border-amber-100 hover:border-[#f9cb07]",
  },
  {
    emoji: "🔍",
    problem: "Not showing up on Google",
    detail: "Your competitor is on page 1. You're on page 3 — or not there at all. Most customers never find you.",
    fix: "SEO Services gets you ranking for local suburb searches where your next customer is already looking.",
    tool: "SEO Services",
    href: "/contact",
    cta: "Fix My SEO",
    accent: "from-blue-50 to-sky-50",
    border: "border-blue-100 hover:border-[#f9cb07]",
  },
  {
    emoji: "📋",
    problem: "Too much manual admin",
    detail: "Quotes by hand, follow-ups in your head, invoices chased by phone. Half your week goes to admin, not the actual job.",
    fix: "Business Automation handles the repeat tasks — follow-ups, reminders, scheduling — without you touching it.",
    tool: "Business Automation",
    href: "/contact",
    cta: "Automate This",
    accent: "from-emerald-50 to-teal-50",
    border: "border-emerald-100 hover:border-[#f9cb07]",
  },
];

export default function IndustriesTeaser() {
  return (
    <section className="py-20 bg-[#f7f8fb]">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="codeteki-pill mb-6">Sound Familiar?</span>
          <h2 className="text-3xl lg:text-4xl font-bold text-black mb-3">
            Four problems that cost Australian businesses thousands every month
          </h2>
          <p className="text-[#52525b]">
            We don't care what industry you're in. If any of these sound like your business, we can fix it.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-4xl mx-auto">
          {problems.map((p) => (
            <div
              key={p.problem}
              className={`group rounded-2xl border bg-gradient-to-br ${p.accent} ${p.border} p-6 flex flex-col gap-4 transition-all duration-200 hover:shadow-[0_8px_30px_rgba(249,203,7,0.12)]`}
            >
              <div className="flex items-start gap-3">
                <span className="text-3xl shrink-0">{p.emoji}</span>
                <h3 className="text-lg font-bold text-[#09090b] leading-snug">{p.problem}</h3>
              </div>
              <p className="text-sm text-[#52525b] leading-relaxed">{p.detail}</p>
              <div className="rounded-xl bg-white/70 border border-white p-3">
                <p className="text-xs font-semibold text-[#f9cb07] uppercase tracking-wide mb-1">How we fix it</p>
                <p className="text-sm text-[#09090b]">
                  <span className="font-semibold">{p.tool}</span> — {p.fix.replace(`${p.tool} `, "")}
                </p>
              </div>
              <Button
                asChild
                className="mt-auto w-full bg-[#0f172a] text-white hover:bg-[#f9cb07] hover:text-black font-semibold transition-all duration-200"
              >
                <a href={p.href}>
                  {p.cta}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </Button>
            </div>
          ))}
        </div>

        <p className="text-center text-[#9ca3af] text-sm mt-10">
          These problems show up in every industry — trades, health, hospitality, professional services, retail, and more.{" "}
          <a href="/get-started" className="text-[#0f172a] font-semibold hover:text-[#f9cb07] transition-colors">
            Tell us yours →
          </a>
        </p>
      </div>
    </section>
  );
}
